import { useQueryClient } from '@tanstack/react-query';
import { S3_URL } from 'constants';
import { IMAGE_PATHS } from 'constants/imagePaths';
import useTranslate from 'hooks/useTranslate';
import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDeleteChatRoomMutation, useReportMutation } from 'services';
import useAuthStore from 'store/auth';
import useChatStore from 'store/chat';
import useCommonStore, { setModalConfig } from 'store/common';
import { blockUser, unblockUser } from 'store/socket';
import useZegoCloud from 'hooks/useZegoCloud';

const Chat: React.FC<any> = ({
	refetch,
	receiverDetails,
	isBlock = false,
	blockUserIds = [],
	isReported = false,
}) => {
	const { isTypingRooms }: Record<string, any> = useChatStore((state) => state || []);
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { translate } = useTranslate();
	const { description, id, name, profilePicture, userType } = receiverDetails;
	const { mutateAsync: deleteChatRoomMutation } = useDeleteChatRoomMutation();
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { roomId }: any = useChatStore((state) => state);
	const { mutateAsync: reportProfileMutation } = useReportMutation();
	const [userBlocked, setUserBlocked] = useState(false);
	const [userReported, setUserReported] = useState(false);
	const { invite } = useZegoCloud();

	useEffect(() => {
		setUserBlocked(isBlock || false);
		setUserReported(isReported || false);
	}, [receiverDetails, roomId, isBlock, isReported]);

	const showDeleterModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: deleteChat,
			data: {
				title: translate('Modal.wantToDelete', { title: `${translate('Chat.chat')}` }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
			onClose: null,
		});
	};

	const deleteChat = () => {
		deleteChatRoomMutation(roomId)
			.then((_res: any) => {
				refetch();
				queryClient.invalidateQueries({
					queryKey: ['get-chat-users'],
				});
				hideCommonModal();
			})
			.catch((error) => {
				console.error('Failed to delete chat room:', error);
				toast.error(translate('ToastMessage.failedToDeleteChat')); // Notify the user of the failure
			});
	};

	const reportProfile = () => {
		setModalConfig({
			visible: true,
			id: id,
			onClick: reportProfileFunction,
			type: 'reportFeeds',
			contentType: 'profile',
		});
	};

	const reportProfileFunction = (values: any) => {
		reportProfileMutation({ ...values, contentId: Number(id) })
			.then((res: any) => {
				if (res?.success) {
					toast.success(
						translate(`Profile.profileReported`, { field: translate('Report.profile') }),
					);
				}
				hideCommonModal();
			})
			.catch((err: any) => {});
	};

	const onHandleBlockClick = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			id,
			onClick: () => {
				checkBlockBySelf() ? unblockUser(id) : blockUser(id);
				hideCommonModal();
				setTimeout(() => refetch(), 1000);
			},
			onClose: null,
			data: {
				title: translate('Modal.doYouWantToTitle', {
					title: checkBlockBySelf() ? translate('Report.unblock') : translate('Report.block'),
				}),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const checkBlockBySelf = () => {
		return userBlocked && blockUserIds?.includes(Number(authUser?.userId)) ? true : false;
	};

	const onCallClick = (type: any) => {
		// Start the call
		invite(type, receiverDetails);
	};

	return (
		<div className="chat-box-screen-header d-flex align-items-center justify-content-between gap-3">
			<div
				className="d-flex gap-2 gap-sm-3 align-items-center"
				onClick={() => (id ? navigate(`/${userType}/profile/${id}`) : {})}
			>
				<div className="chat-box-screen-header-img position-relative">
					<Image
						src={profilePicture ? S3_URL + profilePicture : IMAGE_PATHS?.UserDefault}
						alt="profile"
						className="w-100 h-100 object-fit-cover rounded-circle"
						loading="lazy"
					/>
					{/* <span className="active-sign position-absolute"></span> */}
				</div>
				<div className="chat-box-screen-header-content flex-1">
					<div className="d-flex align-items-center">
						<h5 className="fw-bold mb-1">{name}</h5>
						{isTypingRooms?.includes(roomId) && <span>{translate('Chat.isTyping')}...</span>}
					</div>

					{description && <p className="chat-time mb-0">{description}</p>}
				</div>
			</div>
			<div className="chat-box-screen-header-actions d-flex">
				{!userReported && !userBlocked && (
					<>
						<Button onClick={() => onCallClick(0)} className="edit-btn border-0 p-0 rounded-circle">
							<i className="ri-phone-line"></i>
						</Button>
						<Button onClick={() => onCallClick(1)} className="edit-btn border-0 p-0 rounded-circle">
							<i className="ri-vidicon-line"></i>
						</Button>
					</>
				)}
				<div className="edit-action">
					<Dropdown>
						<Dropdown.Toggle
							variant="success"
							id="dropdown-basic"
							className="p-0 border-0 rounded-circle"
						>
							<i className="ri-more-2-fill"></i>
						</Dropdown.Toggle>

						<Dropdown.Menu>
							<Dropdown.Item onClick={() => showDeleterModal()}>
								<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
							</Dropdown.Item>
							<Dropdown.Item onClick={reportProfile}>
								<i className="ri-flag-line"></i> {translate('Report.report')}
							</Dropdown.Item>
							<Dropdown.Item onClick={() => onHandleBlockClick()}>
								<i className="ri-spam-2-line"></i>{' '}
								{checkBlockBySelf() ? translate('Report.unblock') : translate('Report.block')}
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>
		</div>
	);
};

export default Chat;
